import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Package2, Upload, Search, History, AlertTriangle, Check, Plus, Loader2, X, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const UPLOAD_ROLES = ["ADMIN", "SALES_MANAGER", "ACCOUNTS"];
const DELETE_ROLES = ["ADMIN"];

function formatQty(q, unit) {
  const n = Number(q || 0);
  const abs = Math.abs(n).toLocaleString("en-IN");
  return `${n < 0 ? "-" : ""}${abs} ${unit || "nos"}`;
}
function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Stock() {
  const { user } = useAuth();
  const canUpload = UPLOAD_ROLES.includes(user?.role);
  const canDelete = DELETE_ROLES.includes(user?.role);

  const [data, setData] = useState({ items: [], total_products: 0, total_quantity: 0, negative_count: 0, unit: "nos" });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock", { params: search ? { search } : {} });
      setData(data);
    } catch (e) {
      toast.error("Could not load stock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/stock/${deleteTarget.id}`);
      toast.success(`Removed ${deleteTarget.name}`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete product");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="stock-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Stock Management</h1>
          <p className="text-sm text-slate-500">Current stock from Tally. Upload a new statement to replace quantities.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="stock-history-btn" className="gap-2">
            <History className="h-4 w-4" /> History
          </Button>
          {canUpload && (
            <Button onClick={() => setUploadOpen(true)} data-testid="stock-upload-btn" className="gap-2" style={{ backgroundColor: "#0057A8" }}>
              <Upload className="h-4 w-4" /> Upload Stock
            </Button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Products" value={data.total_products} icon={Package2} />
        <StatCard label={`Total Quantity (${data.unit})`} value={Number(data.total_quantity || 0).toLocaleString("en-IN")} />
        <StatCard label="Negative Stock" value={data.negative_count} tone={data.negative_count > 0 ? "warn" : undefined} icon={data.negative_count > 0 ? AlertTriangle : undefined} />
        <StatCard label="Last Upload" value={data.items[0]?.last_updated_at ? formatDate(Math.max(...data.items.map(i => +new Date(i.last_updated_at || 0)))) : "—"} small />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product name..."
          data-testid="stock-search-input" className="pl-9" />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stock-table">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Product Name</th>
                <th className="px-4 py-3 text-right">Quantity</th>
                <th className="px-4 py-3">Last Updated</th>
                {canDelete && <th className="px-4 py-3 w-14 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canDelete ? 4 : 3} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={canDelete ? 4 : 3} className="px-4 py-10 text-center text-slate-400">No products found</td></tr>
              ) : (
                data.items.map((it) => {
                  const neg = Number(it.quantity) < 0;
                  return (
                    <tr key={it.id} data-testid={`stock-row-${it.name_key}`}
                      className={`border-t border-slate-100 ${neg ? "bg-red-50" : "hover:bg-slate-50/60"}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {neg && <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5 text-red-500" />}
                        {it.name}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono tabular-nums ${neg ? "font-bold text-red-600" : "text-slate-700"}`}>
                        {formatQty(it.quantity, it.unit)}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{formatDate(it.last_updated_at)}</td>
                      {canDelete && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setDeleteTarget(it)}
                            data-testid={`stock-delete-${it.name_key}`}
                            title="Delete product"
                            className="rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {uploadOpen && (
        <UploadDialog onClose={() => setUploadOpen(false)} onApplied={() => { setUploadOpen(false); load(); }} />
      )}
      <HistorySheet open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete product?</DialogTitle>
            <DialogDescription asChild>
              <div className="pt-2 text-sm text-slate-600">
                <div className="font-semibold text-slate-800">{deleteTarget?.name}</div>
                <div className="mt-1">Current quantity: <span className="font-mono">{deleteTarget && formatQty(deleteTarget.quantity, deleteTarget.unit)}</span></div>
                <div className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                  This product will be removed from the stock list. If it appears in a future Tally upload it will be re-created.
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button onClick={doDelete} disabled={deleting} data-testid="stock-delete-confirm"
              className="bg-red-600 text-white hover:bg-red-700">
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, small }) {
  const toneCls = tone === "warn" ? "bg-red-50 text-red-700 ring-red-100" : "bg-white ring-slate-200";
  return (
    <div className={`rounded-xl ring-1 p-4 ${toneCls}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className={`mt-2 font-semibold ${small ? "text-sm" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

function UploadDialog({ onClose, onApplied }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (f) => {
    if (!f) return;
    setFile(f); setPreview(null);
  };

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/stock/upload/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read file");
    } finally {
      setBusy(false);
    }
  };

  const doApply = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/stock/upload/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Stock updated — ${data.updated_count} changed, ${data.added_count} added`);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Upload Stock Statement</DialogTitle></DialogHeader>

        {!preview && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload a CSV or Excel file exported from Tally. Quantities will <strong>replace</strong> existing values (not be added).</p>
            <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center">
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                onChange={(e) => pick(e.target.files?.[0])} data-testid="stock-file-input" />
              <Button variant="outline" onClick={() => inputRef.current?.click()} data-testid="stock-file-pick">
                <Upload className="mr-2 h-4 w-4" />{file ? "Change file" : "Choose file"}
              </Button>
              {file && <div className="mt-3 text-sm text-slate-600">{file.name} <span className="text-slate-400">({Math.round(file.size / 1024)} KB)</span></div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={doPreview} disabled={!file || busy} data-testid="stock-preview-btn" style={{ backgroundColor: "#0057A8" }}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Preview
              </Button>
            </DialogFooter>
          </div>
        )}

        {preview && (
          <div className="space-y-3" data-testid="stock-preview">
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <SummaryCell label="Parsed" value={preview.parsed_rows} />
              <SummaryCell label="To Update" value={preview.updated.length} tone="warn" />
              <SummaryCell label="New" value={preview.added.length} tone="info" />
              <SummaryCell label="Unchanged" value={preview.unchanged_count} />
            </div>
            <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                  <tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Before</th><th className="px-3 py-2 text-right">After</th><th className="px-3 py-2 text-center">Change</th></tr>
                </thead>
                <tbody>
                  {preview.updated.map((u) => <DiffRow key={u.name_key} row={u} unit={preview.unit} />)}
                  {preview.added.map((a) => <DiffRow key={a.name_key} row={a} unit={preview.unit} isNew />)}
                  {preview.updated.length + preview.added.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No changes — all quantities match current stock.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)}><X className="mr-1 h-4 w-4" />Back</Button>
              <Button onClick={doApply} disabled={busy} data-testid="stock-apply-btn" style={{ backgroundColor: "#0057A8" }}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Confirm & Apply
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value, tone }) {
  const cls = tone === "warn" ? "bg-amber-50 text-amber-700" : tone === "info" ? "bg-sky-50 text-sky-700" : "bg-slate-50 text-slate-700";
  return <div className={`rounded-lg p-2 ${cls}`}><div className="text-xs uppercase tracking-wide opacity-70">{label}</div><div className="text-lg font-semibold">{value}</div></div>;
}

function DiffRow({ row, unit, isNew }) {
  const after = Number(row.after);
  const neg = after < 0;
  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 font-medium text-slate-800">
        {isNew && <span className="mr-1.5 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-700">New</span>}
        {row.name}
      </td>
      <td className="px-3 py-2 text-right font-mono text-slate-500">{isNew ? "—" : formatQty(row.before, unit)}</td>
      <td className={`px-3 py-2 text-right font-mono font-semibold ${neg ? "text-red-600" : "text-slate-800"}`}>{formatQty(after, unit)}</td>
      <td className="px-3 py-2 text-center">
        {isNew ? <Plus className="mx-auto h-4 w-4 text-sky-600" /> : (
          <span className={`font-mono text-xs ${after > row.before ? "text-emerald-600" : "text-red-600"}`}>
            {after - row.before > 0 ? "+" : ""}{after - row.before}
          </span>
        )}
      </td>
    </tr>
  );
}

function HistorySheet({ open, onClose }) {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/stock/uploads").then((r) => setList(r.data || [])).catch(() => toast.error("Could not load history")).finally(() => setLoading(false));
  }, [open]);
  const openDetail = async (uid) => {
    try { const { data } = await api.get(`/stock/uploads/${uid}`); setDetail(data); }
    catch { toast.error("Could not load upload"); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && (setDetail(null), onClose())}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle>Upload History</SheetTitle></SheetHeader>
        {!detail ? (
          <div className="mt-4 space-y-2">
            {loading ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : list.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">No uploads yet</div>
            ) : list.map((u) => (
              <button key={u.id} onClick={() => openDetail(u.id)} data-testid={`history-row-${u.id}`}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left hover:bg-slate-50">
                <div>
                  <div className="font-medium text-slate-800">{u.filename || "Upload"}</div>
                  <div className="text-xs text-slate-500">{formatDate(u.created_at)} • {u.uploaded_by_name}</div>
                </div>
                <div className="text-right text-xs">
                  <div className="font-semibold text-amber-700">{u.updated_count} updated</div>
                  <div className="font-semibold text-sky-700">{u.added_count} added</div>
                  {u.deleted_count ? <div className="font-semibold text-red-700">{u.deleted_count} deleted</div> : null}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <Button variant="outline" size="sm" onClick={() => setDetail(null)}><X className="mr-1 h-3.5 w-3.5" />Back</Button>
            <div className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="font-semibold text-slate-800">{detail.filename}</div>
              <div className="text-xs text-slate-500">{formatDate(detail.created_at)} by {detail.uploaded_by_name}</div>
              <div className="mt-2 text-xs text-slate-500">{detail.updated_count} updated, {detail.added_count} added, {detail.unchanged_count} unchanged</div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                  <tr><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Before</th><th className="px-3 py-2 text-right">After</th></tr>
                </thead>
                <tbody>
                  {(detail.changes || []).map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        {c.action === "added" && <span className="mr-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-700">New</span>}
                        {c.action === "deleted" && <span className="mr-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">Del</span>}
                        {c.name}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">{c.before == null ? "—" : formatQty(c.before, "nos")}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${c.after == null ? "text-slate-400" : (Number(c.after) < 0 ? "text-red-600" : "text-slate-800")}`}>{c.after == null ? "—" : formatQty(c.after, "nos")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
